import * as Sentry from '@sentry/react';

// Sentry utility functions for better error reporting
export const sentryUtils = {
  // Capture exceptions with better context
  captureException: (error, context = {}) => {
    // Don't capture in development
    if (import.meta.env.DEV) {
      console.error('Sentry would capture:', error, context);
      return;
    }

    Sentry.withScope((scope) => {
      // Add user context
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.id) {
        scope.setUser({
          id: user.id,
          email: user.email,
          username: user.userName,
          companyId: user.companyId,
        });
      }

      // Add custom context
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });

      // Add tags for better filtering
      scope.setTag('error_type', 'exception');
      scope.setTag('environment', import.meta.env.MODE);

      Sentry.captureException(error);
    });
  },

  // Capture messages with context
  captureMessage: (message, level = 'info', context = {}) => {
    // Don't capture in development
    if (import.meta.env.DEV) {
      console.log('Sentry would capture message:', message, level, context);
      return;
    }

    Sentry.withScope((scope) => {
      // Add user context
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.id) {
        scope.setUser({
          id: user.id,
          email: user.email,
          username: user.userName,
        });
      }

      // Add custom context
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });

      // Add tags
      scope.setTag('error_type', 'message');
      scope.setTag('environment', import.meta.env.MODE);

      Sentry.captureMessage(message, level);
    });
  },

  // Set user context globally
  setUser: (user) => {
    if (!import.meta.env.DEV) {
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.userName,
        companyId: user?.companyId,
      });
    }
  },

  // Add breadcrumb for better debugging
  addBreadcrumb: (category, message, data = {}) => {
    if (!import.meta.env.DEV) {
      Sentry.addBreadcrumb({
        category,
        message,
        data,
        level: 'info',
      });
    }
  },

  // Performance monitoring
  startTransaction: (name, operation) => {
    if (!import.meta.env.DEV) {
      return Sentry.startTransaction({
        name,
        op: operation,
      });
    }
    return null;
  },

  // API error tracking
  trackApiError: (error, endpoint, method, requestData = {}) => {
    sentryUtils.captureException(error, {
      api: {
        endpoint,
        method,
        requestData: import.meta.env.DEV ? requestData : undefined, // Don't send sensitive data in production
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      },
    });
  },

  // Form validation error tracking
  trackValidationError: (errors, formName, formData = {}) => {
    sentryUtils.captureMessage('Form validation failed', 'warning', {
      form: {
        name: formName,
        errors: Object.keys(errors),
        fieldCount: Object.keys(formData).length,
      },
    });
  },

  // Navigation tracking
  trackNavigation: (from, to) => {
    sentryUtils.addBreadcrumb('navigation', `Navigated from ${from} to ${to}`, {
      from,
      to,
      timestamp: new Date().toISOString(),
    });
  },
};

export default sentryUtils;
